import React, { FormEventHandler, useEffect } from 'react';
import { reduxForm, getFormValues, Field, change, FormSection, FormErrors } from 'redux-form';
import { AddressSearchSwitch, Col, FormContainer, Row } from '../styles';
import { SearchWrapper } from './style';
import {
  activateLoader,
  disableLoader
} from '../../../../../redux/actions/ui-trigger/ui-trigger-action';
import { connect } from 'react-redux';
import {
  changeOnBoardingScreen,
  submitAddressDetails,
  submitAddress
} from '../../../../../redux/actions/onboarding/new-onboarding-action';
import { validateAddressForm } from '@Utils/validation';
import { FormValuesProps } from '../Interfaces/interface';
import { KeyValueConstants, OnboardingStepperCmsData } from '../utils/Interface';
import { AddressTypes, IStore } from '../../../../../store.type';
import { MANUAL_ADDRESS_FORM } from '../../../../../utils/constants';
import AddressFields from './AddressFields';
import { CheckboxField, CommonButton } from '@Components';
import {
  ADDRESS_DETAILS_FILLED,
  ADDRESS_PAGE,
  FLOW_TYPE_ONBOARDING
} from '../../../../../utils/constants/eventsConstants';
import { getNewUserFlow } from '../../../../../redux/actions/user-state/user-state-action';
import { logEvent } from '../../../../../utils/events/logUserEvents';
import { LOG_TYPE } from '../../../../../utils/events/eventsConstants';
import { FILLED_CHECKBOX_ICON } from '@Images';

type ManualAddressFormTypes = {
  retrievedValues: AddressTypes;
  addressSearchHandler: (data: boolean) => void;
  initialize: (data: any) => void;
  submitAddressDetails: (data: FormValuesProps) => any;
  submitAddress: (data: FormValuesProps) => any;
  activateLoader: () => void;
  disableLoader: () => void;
  changeOnBoardingScreen: (screenName: string) => void;
  handleSubmit: (func: any) => FormEventHandler<HTMLElement> | undefined;
  cmsData: OnboardingStepperCmsData | undefined;
  cmsInputFields: any[];
  getAllformVal: any;
  dispatch: any;
  constantText: KeyValueConstants;
  countryCode: string;
  contentCountryCode: string;
  addressConfig: AddressTypes;
  getNewUserFlow: any;
  isBeneAddressFlow?: boolean;
  onSubmitBeneFlow?: () => void;
  handleAddressToggle?: () => void;
  isBeneSchemaEnabled?: boolean;
  isRFIflow?: boolean;
  onRFIBack?: () => void;
  onRFISave?: (data: any) => void;
  isOddAddressFlow?: boolean;
  onSubmitOddFlow?: (payload: FormValuesProps) => void;
  oddAddressFieldValidators?: Array<(value: string) => string | undefined>;
  oddFormMarginTop?: number;
};

const ManualAddressForm: React.FC<ManualAddressFormTypes> = ({
  retrievedValues,
  activateLoader,
  disableLoader,
  submitAddressDetails,
  submitAddress,
  handleSubmit,
  addressSearchHandler,
  changeOnBoardingScreen,
  initialize,
  cmsData,
  cmsInputFields,
  getAllformVal,
  dispatch,
  constantText,
  countryCode,
  addressConfig,
  getNewUserFlow,
  contentCountryCode,
  isBeneAddressFlow,
  onSubmitBeneFlow,
  handleAddressToggle,
  isBeneSchemaEnabled,
  isRFIflow,
  onRFIBack,
  onRFISave,
  isOddAddressFlow,
  onSubmitOddFlow,
  oddAddressFieldValidators,
  oddFormMarginTop
}: ManualAddressFormTypes) => {
  const {
    unit_number,
    street_number,
    street_name,
    building_name,
    building_number,
    suburb,
    state,
    city,
    postcode,
    is_mailing_address_diff,
    residential,
    mailing,
    address1,
    address2,
    province
  } = retrievedValues;

  // ODD address schemas (e.g. HK) carry fields beyond the onboarding set below — the schema
  const oddSchemaFieldValues: Record<string, unknown> = isOddAddressFlow
    ? Object.fromEntries(
        (Object.values(addressConfig ?? {}) as any[])
          .map((item) => item?.render?.value_field_name)
          .filter(Boolean)
          .map((name: string) => [name, (retrievedValues as Record<string, any>)[name]])
      )
    : {};

  const initialFormValues = {
    ...oddSchemaFieldValues,
    unit_number,
    street_number,
    street_name,
    building_name,
    building_number,
    suburb,
    state,
    city,
    postcode,
    is_mailing_address_diff,
    residential,
    mailing,
    address1,
    address2,
    province
  };

  const handleAddressSearchSwitchCallBack = () => {
    addressSearchHandler(false);
  };

  useEffect(() => {
    if (isBeneAddressFlow && getAllformVal) return;
    initialize(initialFormValues);
    // eslint-disable-next-line
  }, []);

  const logEvents = () => {
    const ctData = {
      pageName: ADDRESS_PAGE
    };
    logEvent(ADDRESS_DETAILS_FILLED, FLOW_TYPE_ONBOARDING, ctData, LOG_TYPE.CLEVER_TAP);
  };

  const submitAUAddress = async () => {
    if (isRFIflow && onRFISave) {
      onRFISave(getAllformVal);
      return;
    }
    activateLoader();
    const payload = prepareSubmitPayload();
    const response = await submitAddressDetails(payload);
    if (response?.data?.success) {
      logEvents();
      if (isRFIflow) {
        onRFIBack?.();
      } else {
        const nextScreenName = response?.data?.data?.nextOnboardProcess?.transitions[0]?.to;
        if (nextScreenName) changeOnBoardingScreen(nextScreenName);
      }
    }
    disableLoader();
  };

  const prepareSubmitPayload = () => {
    const payload: { [key: string]: any } = {};

    const convertToCamelCase = (str: string) => {
      return str.replace(/_([a-z])/g, (g) => g[1].toUpperCase());
    };

    // Dynamically build payload from addressConfig and form values
    if (addressConfig && getAllformVal) {
      Object.keys(addressConfig).forEach((key) => {
        const configItem = addressConfig[key as keyof AddressTypes];
        const fieldName = configItem?.render?.value_field_name;
        const payloadKey = convertToCamelCase(fieldName);
        if (
          fieldName &&
          getAllformVal[fieldName] !== undefined &&
          getAllformVal[fieldName] !== ''
        ) {
          payload[payloadKey] = getAllformVal[fieldName];
        }
      });

      // Handle special case for mailing address (MY)
      // to fix the mailing address diff issue, we need to send the checkbox
      // value as it is and not under any condition
      if (contentCountryCode === 'MY') {
        payload.isMailingAddressDiff = getAllformVal?.is_mailing_address_diff;
        if (getAllformVal?.is_mailing_address_diff) {
          const mailingAddress: { [key: string]: any } = {};
          Object.keys(addressConfig).forEach((key) => {
            const configItem = addressConfig[key as keyof AddressTypes];
            const fieldName = configItem?.render?.value_field_name;
            const payloadKey = convertToCamelCase(fieldName);

            if (
              fieldName &&
              getAllformVal?.mailing?.[fieldName] !== undefined &&
              getAllformVal?.mailing?.[fieldName] !== ''
            ) {
              mailingAddress[payloadKey] = getAllformVal.mailing[fieldName];
            }
          });

          payload.mailingAddress = mailingAddress;
        }
      }
    }

    return payload;
  };

  const submitOtherAddress = async () => {
    if (isRFIflow && onRFISave) {
      onRFISave(getAllformVal);
      return;
    }
    activateLoader();
    const payload = prepareSubmitPayload();
    const response = await submitAddress(payload);
    if (response?.data?.success) {
      getNewUserFlow();
      logEvents();
      if (isRFIflow) {
        onRFIBack?.();
      } else {
        const nextScreenName = response?.data?.data?.nextOnboardProcess?.transitions[0]?.to;
        changeOnBoardingScreen(nextScreenName);
      }
    }
    disableLoader();
  };

  const handleCheckboxFieldChange = async () => {
    await dispatch(change(MANUAL_ADDRESS_FORM, 'mailing', null));
    await dispatch(
      change(
        MANUAL_ADDRESS_FORM,
        'is_mailing_address_diff',
        !getAllformVal?.is_mailing_address_diff
      )
    );
  };

  const submitForm = () => {
    if (isOddAddressFlow) {
      onSubmitOddFlow?.(getAllformVal);
      return;
    }
    // Call different submit functions based on countryCode
    if (countryCode === 'AU') {
      submitAUAddress();
    } else {
      submitOtherAddress();
    }
  };

  if (isBeneAddressFlow) {
    return (
      <form onSubmit={handleSubmit(onSubmitBeneFlow)}>
        <AddressFields
          addressConfig={addressConfig}
          dispatch={dispatch}
          getAllformVal={getAllformVal}
          countryCode={countryCode}
          cmsInputFields={cmsInputFields}
          contentCountryCode={contentCountryCode}
          isBeneAddressFlow={isBeneAddressFlow}
          isBeneSchemaEnabled={isBeneSchemaEnabled}
        />

        <SearchWrapper>
          <CommonButton text="Go back to search" type="link" onClick={handleAddressToggle} />
        </SearchWrapper>
        <CommonButton text="Submit" width={450} />
      </form>
    );
  }

  return (
    <FormContainer marginTop={oddFormMarginTop ?? 45}>
      <form onSubmit={handleSubmit(submitForm)}>
        <AddressFields
          addressConfig={addressConfig}
          dispatch={dispatch}
          getAllformVal={getAllformVal}
          countryCode={countryCode}
          cmsInputFields={cmsInputFields}
          contentCountryCode={contentCountryCode}
          extraValidators={oddAddressFieldValidators}
        />

        {countryCode === 'MY' && (
          <Row padding={[35, 0, 0, 3]}>
            <Col width={450}>
              <Field
                name="is_mailing_address_diff"
                checked={getAllformVal?.is_mailing_address_diff}
                component={CheckboxField}
                broderBorder={true}
                customImage={FILLED_CHECKBOX_ICON}
                handleChange={handleCheckboxFieldChange}
                invertedBehavior={true}
                width={24}
                height={24}
                checkboxContent={<p className="checkBoxMessage">{constantText?.checkBoxText}</p>}
              />
            </Col>
          </Row>
        )}

        {countryCode === 'MY' && getAllformVal?.is_mailing_address_diff && (
          <div className="mailingAddressFields">
            <FormSection name="mailing" className="mailingAddressFields">
              <AddressFields
                addressConfig={addressConfig}
                dispatch={dispatch}
                getAllformVal={getAllformVal}
                countryCode={countryCode}
                cmsInputFields={cmsInputFields}
                contentCountryCode={contentCountryCode}
              />
            </FormSection>
          </div>
        )}

        {(isOddAddressFlow || countryCode === 'AU' || isRFIflow) && (
          <AddressSearchSwitch onClick={handleAddressSearchSwitchCallBack}>
            {isOddAddressFlow ? 'Go back to search' : cmsData?.secondaryBtnText}
          </AddressSearchSwitch>
        )}

        <Row padding={[35, 0, 32, 0]}>
          <Col width={450}>
            <CommonButton
              text={
                isRFIflow ? 'Submit' : isOddAddressFlow ? 'Save' : cmsData?.primaryBtnText ?? ''
              }
              width={450}
              height={46}
            />
          </Col>
        </Row>
      </form>
    </FormContainer>
  );
};

const ManualAddress = reduxForm<FormValuesProps, any>({
  form: MANUAL_ADDRESS_FORM,
  destroyOnUnmount: false,
  validate: validateAddressForm as (
    values: FormValuesProps,
    props: any
  ) => FormErrors<FormValuesProps>
})(ManualAddressForm);

const mapStateToProps = (reduxState: IStore) => {
  const getAllformVal = getFormValues(MANUAL_ADDRESS_FORM)(reduxState);

  return {
    getAllformVal
  };
};
export default connect<any, any, any, IStore>(mapStateToProps, {
  submitAddressDetails,
  submitAddress,
  activateLoader,
  disableLoader,
  changeOnBoardingScreen,
  getNewUserFlow
})(ManualAddress);
